import React from 'react';
import * as PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { selectAddWeightDataPending } from '../../../../redux/profiles/patientHealthboard/selectors';
import * as addWeightData from '../../../../redux/profiles/patientHealthboard/add-weight-data';
import { useFormState } from '../../../../hocs/useFormWithValidation';
import { COLORS } from '../../../../constants/styles';
import { IDS } from '../../../../constants/ids';
import DatePickerCustom from '../../../common/DatePickerCustom';
import TimePickerInput from '../../../common/TimePickerInput';
import Loader from '../../../common/Loader';
import TextInput from '../../../TextInput';
import Button from '../../../Button';
import Icon from '../../../Icon';
import {
  convertValueWeightToKg,
  getCurrentTime,
  validateFutureTime,
} from '../../utils';
import styles from '../index.styl';

const {
  PATIENT_PORTAL: { ADD_WEIGHT_FORM },
} = IDS;

export const AddWeightForm = ({ formatMessage, modalMode, isWeightInKg }) => {
  const currentTime = moment().format('h:mm A');
  const { formState, handleFormChange } = useFormState({
    [ADD_WEIGHT_FORM.TIME]: currentTime,
  });
  const currentDate = new Date();

  const dispatch = useDispatch();
  const pending = useSelector(selectAddWeightDataPending);

  const combinedDisable =
    !formState[ADD_WEIGHT_FORM.WEIGHT] ||
    !formState[ADD_WEIGHT_FORM.TIME] ||
    pending;

  const handleSubmit = e => {
    e.preventDefault();
    const safeDate = moment(formState[ADD_WEIGHT_FORM.DATE]).format(
      'YYYY-MM-D',
    );

    const safeTime = formState[ADD_WEIGHT_FORM.TIME]
      ? `T${formState[ADD_WEIGHT_FORM.TIME]}`
      : `T${moment().format('hh:mm A')}`;

    const weightValue = isWeightInKg
      ? parseInt(formState[ADD_WEIGHT_FORM.WEIGHT], 10)
      : convertValueWeightToKg(formState[ADD_WEIGHT_FORM.WEIGHT]);

    dispatch(
      addWeightData.actions.send({
        local_weight_measurement: {
          measured_at: moment(
            safeDate + safeTime,
            'YYYY-MM-DThh:mm A',
          ).format(),
          value: { baseValue: weightValue },
        },
      }),
    );

    handleFormChange({ [ADD_WEIGHT_FORM.WEIGHT]: '' });
  };

  return (
    <div className={cn(styles.root, { [styles.rootModalMode]: modalMode })}>
      {!modalMode && (
        <div className={styles.title}>
          {formatMessage(
            { id: 'patient.portal.emptyState.addData' },
            { type: formatMessage({ id: 'patient.healthboard.weight' }) },
          )}
        </div>
      )}
      {pending && <Loader className={styles.loader} />}
      <form
        className={cn(styles.content, {
          [styles.modalMode]: modalMode,
        })}
        onSubmit={handleSubmit}
      >
        <div className={styles.dateWrapper}>
          <div className={styles.datePickerWrapper}>
            <DatePickerCustom
              value={formState[ADD_WEIGHT_FORM.DATE] || currentDate}
              label={formatMessage({ id: 'common.date' })}
              name={ADD_WEIGHT_FORM.DATE}
              maxDate={new Date()}
              formatDate="MMM D, YYYY"
              onChange={value =>
                handleFormChange({
                  [ADD_WEIGHT_FORM.DATE]: value,
                  [ADD_WEIGHT_FORM.TIME]: '',
                })
              }
              disabled={pending}
            />
            <Icon
              name="calendar"
              style={{ fill: COLORS.bluePrimary }}
              className={styles.datePickerIcon}
            />
          </div>
          <TimePickerInput
            value={formState[ADD_WEIGHT_FORM.TIME]}
            name={ADD_WEIGHT_FORM.TIME}
            onChange={value =>
              handleFormChange({ [ADD_WEIGHT_FORM.TIME]: value.toUpperCase() })
            }
            label={formatMessage({ id: 'reports.time' })}
            placeholder={formatMessage({ id: 'reports.time' })}
            className={styles.timePickerWrapper}
            disabled={pending}
            validateTime={pickTime =>
              validateFutureTime(
                formatMessage,
                pickTime,
                formState[ADD_WEIGHT_FORM.DATE],
              )
            }
            time={getCurrentTime()}
          />
        </div>
        <TextInput
          id={ADD_WEIGHT_FORM.WEIGHT}
          type="number"
          label={formatMessage({ id: 'patient.healthboard.weight' })}
          limit={5}
          isFractionNumber
          placeholder={formatMessage({
            id: `profile.section.unitsSettings.weight.metric.${
              isWeightInKg ? 'kilograms' : 'pounds'
            }`,
          })}
          value={formState[ADD_WEIGHT_FORM.WEIGHT]}
          onChange={value =>
            handleFormChange({ [ADD_WEIGHT_FORM.WEIGHT]: value })
          }
          className={styles.weightInput}
          disabled={pending}
          min={1}
        />
        <Button
          id={ADD_WEIGHT_FORM.ADD_BUTTON}
          raised
          color="primary"
          className={styles.addButton}
          disabled={combinedDisable}
        >
          {formatMessage({ id: 'common.add' })}
        </Button>
      </form>
    </div>
  );
};

AddWeightForm.propTypes = {
  formatMessage: PropTypes.func,
  modalMode: PropTypes.bool,
  isWeightInKg: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/Forms/AddWeightForm/index.jsx